import React, { useEffect } from "react";

import { FaArrowLeft, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import { Link } from "react-router-dom";

import { trackPrivacyPolicyView } from "../utils/analytics";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    trackPrivacyPolicyView();
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const handleBackToTop = (): void => {
    window.scrollTo(0, 0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-lg shadow-lg border-b border-pink-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link
            to="/"
            onClick={handleBackToTop}
            className="flex items-center gap-3 text-purple-600 hover:text-purple-800 transition-colors duration-200 font-medium"
          >
            <FaArrowLeft className="text-lg" />
            <span>Back to Home</span>
          </Link>
          <div className="flex justify-end">
            <img src="/logo.svg" alt="Cohop Logo" className="h-12 w-auto" />
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg border border-pink-100 p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-purple-800 font-display mb-4">
              Privacy Policy
            </h1>
            <p className="text-gray-600 text-lg">
              Last updated:{" "}
              <span className="font-semibold">August 22, 2025</span>
            </p>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-gray-700 leading-relaxed">
              Cohop (&quot;we,&quot; &quot;our,&quot; &quot;us&quot;) values
              your trust. This Privacy Policy explains how we collect, use, and
              protect your personal information when you use our app, website,
              WhatsApp onboarding, and related services.
            </p>
          </div>

          <hr className="border-pink-200 my-8" />

          {/* Section 1 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              1. Information We Collect
            </h2>
            <p className="text-gray-700 mb-4">
              We collect the following types of information:
            </p>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-purple-700 mb-3">
                  a. Information You Provide
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Name, age, and WhatsApp number.</li>
                  <li>
                    Verification documents (boarding pass/flight ticket or
                    selfie with government ID).
                  </li>
                  <li>Trip details you enter (origin, destination, time).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-purple-700 mb-3">
                  b. Information We Collect Automatically
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    Location data (only during active trips, if you allow
                    location access).
                  </li>
                  <li>
                    Device and usage information (app version, device type, log
                    data).
                  </li>
                  <li>Ride activity (past trips, matched co-travellers).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-purple-700 mb-3">
                  c. Information from Other Sources
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    Identity verification results from third-party services.
                  </li>
                  <li>Feedback or ratings from other users.</li>
                </ul>
              </div>
            </div>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 2 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              2. Document Verification & Security
            </h2>
            <p className="text-gray-700 mb-4">
              We use document verification to ensure community safety and
              authenticity of all members.
            </p>
            <p className="text-gray-700 mb-4">
              Here&apos;s how we handle your verification documents:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-3 ml-4">
              <li>
                <strong>Boarding Pass/Flight Ticket:</strong> Used to verify
                you&apos;re actually traveling and match you with co-travelers
                on the same route.
              </li>
              <li>
                <strong>Government ID Selfie:</strong> Used for identity
                verification to ensure you are who you claim to be.
              </li>
              <li>
                <strong>Secure Storage:</strong> All documents are encrypted and
                stored securely on Firebase Storage with restricted access.
              </li>
              <li>
                <strong>Limited Access:</strong> Only authorized verification
                team members can access documents for review.
              </li>
              <li>
                <strong>Document Retention:</strong> Documents are deleted after
                verification when possible, or retained only as long as
                necessary for safety.
              </li>
              <li>
                <strong>No Sharing:</strong> Your verification documents are
                never shared with other users or third parties.
              </li>
            </ul>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800 font-semibold">
                <strong>Privacy First:</strong> We collect and process
                verification documents solely for safety purposes and handle
                them with the highest level of security and confidentiality.
              </p>
            </div>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              3. WhatsApp Data Handling (MVP Phase)
            </h2>
            <p className="text-gray-700 mb-4">
              During our MVP phase, many users join Cohop through{" "}
              <strong>WhatsApp groups</strong> we operate for specific travel
              routes (e.g., Delhi &quot;→&quot; Bengaluru female solo
              travellers).
            </p>
            <p className="text-gray-700 mb-4">
              Here&apos;s how we handle your WhatsApp-related data:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-3 ml-4">
              <li>
                <strong>Phone numbers:</strong> We use your number only to add
                you to relevant WhatsApp groups and send essential ride
                coordination messages.
              </li>
              <li>
                <strong>Group activity:</strong> We do not log or store your
                private chats with other members.
              </li>
              <li>
                <strong>Transition to app:</strong> If you choose to move from
                WhatsApp to the Cohop app, we may use your WhatsApp profile name
                and number to create or verify your Cohop account.
              </li>
              <li>
                <strong>No marketing spam:</strong> We will not share your
                number outside the purpose of ride coordination and safety
                communication.
              </li>
              <li>
                <strong>Leaving the group:</strong> You may exit a WhatsApp
                group at any time. Upon request, we will also delete your number
                from our onboarding records.
              </li>
            </ul>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              4. How We Use Your Information
            </h2>
            <p className="text-gray-700 mb-4">We use your information to:</p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Match you with verified co-travellers.</li>
              <li>Provide real-time trip tracking and safety alerts.</li>
              <li>Facilitate ride coordination and fare-splitting.</li>
              <li>Improve our services and user experience.</li>
              <li>Communicate updates, offers, or important safety notices.</li>
              <li>Comply with legal obligations.</li>
            </ul>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              5. How We Share Your Information
            </h2>
            <p className="text-gray-700 mb-4">
              We only share your information when necessary:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-3 ml-4">
              <li>
                <strong>With matched co-travellers:</strong> Your first name,
                profile info (college, hometown, etc.), and trip details
                relevant to the ride.
              </li>
              <li>
                <strong>With service providers:</strong> For payment processing,
                background verification, or app functionality.
              </li>
              <li>
                <strong>For safety and legal reasons:</strong> If required by
                law, or to protect your safety or the safety of others.
              </li>
            </ul>

            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4 mt-4">
              <p className="text-purple-800 font-semibold">
                We <strong>do not</strong> sell your personal data to third
                parties.
              </p>
            </div>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              6. Your Privacy Choices
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-3 ml-4">
              <li>
                <strong>Profile visibility:</strong> You control what optional
                profile details are shown to matched co-travellers.
              </li>
              <li>
                <strong>Location sharing:</strong> Enabled only during active
                trips and only with matched co-travellers and your trusted
                contacts.
              </li>
              <li>
                <strong>Communication preferences:</strong> You can opt out of
                marketing emails or messages anytime.
              </li>
            </ul>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              7. Data Security
            </h2>
            <p className="text-gray-700">
              We use encryption and secure servers to protect your data.
              However, no method of transmission over the Internet or storage is
              completely secure, so we cannot guarantee absolute security.
            </p>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              8. Data Retention
            </h2>
            <p className="text-gray-700 mb-4">
              We keep your personal information only as long as necessary for:
            </p>

            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Providing services.</li>
              <li>Fulfilling legal obligations.</li>
              <li>Resolving disputes.</li>
            </ul>

            <p className="text-gray-700 mt-4">
              You can request deletion of your account and associated data by
              contacting us.
            </p>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              9. Children&apos;s Privacy
            </h2>
            <p className="text-gray-700">
              Cohop is not intended for individuals under 18 years old. We do
              not knowingly collect data from minors.
            </p>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              10. Changes to This Policy
            </h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. Significant
              changes will be notified via the app, WhatsApp, or email.
            </p>
          </section>

          <hr className="border-pink-200 my-8" />

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-purple-800 font-display mb-4">
              11. Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions or concerns, reach us at:
            </p>

            <div className="bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-200 rounded-lg p-6">
              <div className="flex flex-col space-y-3">
                <div className="flex items-center gap-3">
                  <FaEnvelope className="text-purple-600 text-lg" />
                  <a
                    href="mailto:privacy@cohop.in"
                    className="text-purple-700 hover:text-purple-900 font-medium transition-colors"
                  >
                    privacy@cohop.in
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-purple-600 text-lg" />
                  <span className="text-purple-700 font-medium">
                    Bengaluru, Karnataka
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Back to top / Home button */}
          <div className="text-center pt-8">
            <Link
              to="/"
              onClick={handleBackToTop}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-400 to-purple-500 text-white px-6 py-3 rounded-full hover:from-pink-500 hover:to-purple-600 transition-all duration-300 font-semibold transform hover:scale-105 shadow-lg"
            >
              <FaArrowLeft className="text-sm" />
              Back to Home
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
